import React, { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { ASSIGNMENT_TYPES, RECIPIENT_TYPES, docApi, docErr } from "@/lib/docApi";
import { btnGold, btnRed, field } from "@/components/nds/windows/finance/parts";

export const copyLink = (link) => navigator.clipboard?.writeText(link).then(() => toast.success("Link copied"), () => toast.message(link));

export function LinkBox({ link, emailSent, prefix }) {
  return (
    <div data-testid={`${prefix}-link-box`} className="space-y-1 border border-nex-gold/30 bg-nex-gold/5 rounded-sm p-3">
      <div className="text-xs text-nex-muted">{emailSent ? "Email sent. You can also share this one-time link:" : "Email not sent. Share this one-time link:"}</div>
      <div className="flex gap-2 items-center">
        <input readOnly data-testid={`${prefix}-link`} value={link} className={`${field} flex-1 text-xs`} />
        <button type="button" onClick={() => copyLink(link)} data-testid={`${prefix}-link-copy`} className={`${btnGold} inline-flex items-center gap-1`}><Copy className="h-3 w-3" /> Copy</button>
      </div>
    </div>
  );
}

/** Assign a finalized document to a resident, partner, staff member or public recipient. */
export default function AssignForm({ documents, onCreated, onCancel }) {
  const assignable = documents.filter((d) => ["finalized", "assigned", "signed"].includes(d.status));
  const [f, setF] = useState({ document_id: assignable[0]?.id || "", recipient_type: "public", recipient_id: "", recipient_name: "", recipient_email: "", recipient_phone: "", assignment_type: "signature_required" });
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => {
    if (f.recipient_type === "public") return;
    docApi.get("/recipients", { type: f.recipient_type }).then((d) => setPeople(d.items)).catch((e) => toast.error(docErr(e)));
  }, [f.recipient_type]);
  const pub = f.recipient_type === "public";
  const ok = f.document_id && (pub ? f.recipient_name.trim() && f.recipient_email.includes("@") : f.recipient_id);
  const save = async () => {
    setBusy(true);
    const body = { ...f, recipient_id: pub ? null : f.recipient_id, recipient_name: pub ? f.recipient_name : null,
      recipient_email: f.recipient_email || null, recipient_phone: f.recipient_phone || null };
    try { const d = await docApi.post("/assignments", body); toast.success(`Assigned to ${d.recipient_name}`); onCreated(d); }
    catch (e) { toast.error(docErr(e)); } finally { setBusy(false); }
  };
  if (!assignable.length) return <div data-testid="doc-asg-none" className="p-5 text-sm text-nex-muted space-y-3"><p>No finalized documents yet. Finalize one in the Document Generator first.</p><button type="button" onClick={onCancel} className={btnRed}>Back</button></div>;
  return (
    <div data-testid="doc-asg-form" className="p-5 space-y-3">
      <select data-testid="doc-asg-form-document" className={`${field} w-full`} value={f.document_id} onChange={(e) => set("document_id", e.target.value)}>
        {assignable.map((d) => <option key={d.id} value={d.id}>{d.title} · {d.status}</option>)}
      </select>
      <div className="flex flex-wrap gap-2">
        <select data-testid="doc-asg-form-recipient-type" className={field} value={f.recipient_type} onChange={(e) => setF((x) => ({ ...x, recipient_type: e.target.value, recipient_id: "" }))}>
          {RECIPIENT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="doc-asg-form-type" className={field} value={f.assignment_type} onChange={(e) => set("assignment_type", e.target.value)}>
          {ASSIGNMENT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {pub ? <input data-testid="doc-asg-form-name" className={`${field} w-full`} placeholder="Recipient full name" value={f.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} />
        : <select data-testid="doc-asg-form-recipient" className={`${field} w-full`} value={f.recipient_id} onChange={(e) => set("recipient_id", e.target.value)}>
          <option value="">Select {f.recipient_type}…</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.email ? ` · ${p.email}` : ""}</option>)}
        </select>}
      <div className="flex flex-wrap gap-2">
        <input data-testid="doc-asg-form-email" className={`${field} flex-1`} placeholder={pub ? "Email (required)" : "Email (optional override)"} value={f.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} />
        <input data-testid="doc-asg-form-phone" className={`${field} flex-1`} placeholder="Phone (optional)" value={f.recipient_phone} onChange={(e) => set("recipient_phone", e.target.value)} />
      </div>
      <div className="flex gap-2 pt-2">
        <button type="button" disabled={!ok || busy} onClick={save} data-testid="doc-asg-form-save" className={btnGold}>{busy ? "Assigning…" : "Assign document"}</button>
        <button type="button" onClick={onCancel} data-testid="doc-asg-form-cancel" className={btnRed}>Cancel</button>
      </div>
    </div>
  );
}
